<template>
  <div class="login-page" :class="{ 'page-is-busy': pageLoading }">
    <!-- 全页加载反馈 -->
    <div v-if="pageLoading" class="page-loading-mask" aria-live="polite" aria-busy="true">
      <div class="page-loading-box">
        <span class="page-loading-spinner" aria-hidden="true"></span>
        <p class="page-loading-text">{{ pageLoadingText }}</p>
      </div>
    </div>

    <!-- 顶部品牌区域 -->
    <div class="header">
      <div class="logo">
        <img
          class="logo-img"
          :src="logoUrl"
          alt="自媒V发布"
        />
        <p class="sub-title">金融自媒体人才库</p>
        <p class="desc">成都市自媒体协会官方匹配系统</p>
      </div>
    </div>

    <!-- 登录/注册表单卡片 -->
    <div class="login-card" :class="{ 'is-dimmed': pageLoading }">
      <h2 class="card-title">{{ mode === "login" ? "手机号快捷登录" : "用户注册" }}</h2>

      <!-- 注册：姓名 -->
      <div
        v-if="mode === 'register'"
        ref="field_user_name"
        class="input-group"
        :class="{ 'has-error': !!fieldErrors.user_name }"
      >
        <input
          v-model="registerForm.user_name"
          type="text"
          placeholder="请输入姓名"
          maxlength="32"
          class="phone-input"
          @input="clearFieldError('user_name')"
        />
        <p v-if="fieldErrors.user_name" class="field-error-tip">{{ fieldErrors.user_name }}</p>
      </div>

      <!-- 手机号输入 -->
      <div
        ref="field_phone"
        class="input-group"
        :class="{ 'has-error': !!fieldErrors.phone }"
      >
        <input
          v-model="activePhone"
          type="tel"
          placeholder="请输入手机号"
          maxlength="11"
          class="phone-input"
          @input="onPhoneInput"
          @blur="validatePhoneOnBlur"
        />
        <p v-if="fieldErrors.phone" class="field-error-tip">{{ fieldErrors.phone }}</p>
      </div>

      <!-- 登录：验证码输入 + 获取按钮（注册流程在问卷完成后获取） -->
      <div
        v-if="mode === 'login'"
        ref="field_code"
        class="input-group code-group"
        :class="{ 'has-error': !!fieldErrors.code }"
      >
        <div class="code-input-wrap">
          <input
            v-model="activeCode"
            type="tel"
            placeholder="请输入验证码"
            maxlength="6"
            class="code-input"
            @input="clearFieldError('code')"
          />
          <p v-if="fieldErrors.code" class="field-error-tip">{{ fieldErrors.code }}</p>
        </div>
        <button
          class="code-btn"
          :class="{ 'is-loading': getCodeLoading }"
          :disabled="isCounting || !isPhoneValid || getCodeLoading"
          @click="handleGetCode"
        >
          {{
            getCodeLoading
              ? "发送中…"
              : isCounting
                ? `${countdown}s后重发`
                : "获取验证码"
          }}
        </button>
      </div>

      <button
        class="login-btn btn-press"
        :class="{ 'is-loading': loginLoading }"
        :disabled="loginLoading"
        @click="mode === 'login' ? handleLogin() : handleRegisterNext()"
      >
        {{
          loginLoading
            ? (mode === "login" ? "登录中…" : "校验中…")
            : (mode === "login" ? "登录" : "下一步：填写问卷")
        }}
      </button>
      <button
        type="button"
        class="switch-btn btn-press"
        :disabled="loginLoading || getCodeLoading"
        @click="switchMode(mode === 'login' ? 'register' : 'login')"
      >
        {{ mode === "login" ? "注册" : "登录" }}
      </button>

      <p class="tip">
        {{ mode === "login" ? "登录即代表同意《用户协议》和《隐私政策》" : "请先填写姓名和手机号，下一步完成问卷后可获取验证码并提交注册" }}
      </p>
    </div>
  </div>
</template>

<script>
import { Toast } from 'vant'
import { scrollToFormField } from '@/utils/formScroll'
import {
  markCertSnapshotPending,
  saveLoginSession,
  slimLoginUserInfo,
} from '@/utils/loginSession'
import logoUrl from '@/img/矩形 6.png'

/**
 * API 根地址（无尾斜杠）。
 *
 * 后端按 Docker 分端口（8050=API+管理端、8051=H5+Nginx 反代 /api）或本地 npm run serve（默认见 vue.config.js，如 8053）时：
 * - 用户打开的是「当前页面」的域名与端口，不要改前端里的 IP；未设 VUE_APP_API_BASE 时走同源 /api。
 * - 保持 VUE_APP_API_BASE 为空（推荐）：请求为「当前页面同源」的 `/api/...`，
 *   即自动走 http(s)://当前主机:当前端口/api/...，由 devServer 或 Nginx 再转到 8050，无需写死后端 IP。
 * - 只有需要「浏览器直连 8050、绕过反代」时，才在 .env.* 里设置例如：
 *   VUE_APP_API_BASE=http://你的服务器公网或内网IP:8050
 */
function loginApiBase() {
  return (process.env.VUE_APP_API_BASE || "").trim().replace(/\/$/, "")
}

function loginPhoneUrl() {
  const base = loginApiBase()
  return base ? `${base}/api/login_phone` : "/api/login_phone"
}

export default {
  name: "LoginPage",
  data() {
    return {
      logoUrl,
      mode: "login",
      loginForm: {
        phone: "",
        code: "",
      },
      registerForm: {
        user_name: "",
        phone: "",
        code: "",
      },
      countdown: 60,
      isCounting: false,
      countdownTimer: null,
      getCodeLoading: false,
      loginLoading: false,
      fieldErrors: {},
    };
  },
  computed: {
    activePhone: {
      get() {
        return this.mode === "login" ? this.loginForm.phone : this.registerForm.phone
      },
      set(v) {
        if (this.mode === "login") this.loginForm.phone = v
        else this.registerForm.phone = v
      }
    },
    activeCode: {
      get() {
        return this.mode === "login" ? this.loginForm.code : this.registerForm.code
      },
      set(v) {
        if (this.mode === "login") this.loginForm.code = v
        else this.registerForm.code = v
      }
    },
    isPhoneValid() {
      return /^1[3-9]\d{9}$/.test((this.activePhone || "").trim());
    },
    pageLoading() {
      return this.loginLoading || this.getCodeLoading;
    },
    pageLoadingText() {
      if (this.getCodeLoading) return "正在发送验证码…";
      if (this.loginLoading) {
        return this.mode === "login" ? "正在登录…" : "正在跳转…";
      }
      return "请稍候…";
    },
  },
  beforeDestroy() {
    if (this.countdownTimer) {
      clearInterval(this.countdownTimer);
      this.countdownTimer = null;
    }
  },
  mounted() {
    import(/* webpackPrefetch: true */ '../views/User.vue')
    this.restoreRegisterDraft()
  },
  methods: {
    restoreRegisterDraft() {
      try {
        const raw = sessionStorage.getItem("h5_register_draft")
        if (!raw) return
        const draft = JSON.parse(raw)
        if (!draft || typeof draft !== "object") return
        this.mode = "register"
        if (draft.user_name) this.registerForm.user_name = draft.user_name
        if (draft.phone_number) this.registerForm.phone = draft.phone_number
      } catch (_) {
        /* ignore */
      }
    },
    _normalizeApiMessage(msg) {
      const t = String(msg || "").trim().toLowerCase()
      if (t.includes("check frequency failed")) {
        return "频繁申请验证码，请稍后重试"
      }
      return String(msg || "").trim()
    },
    _toastApiError(e) {
      const p = e && e.payload;
      const detail = p && p.detail;
      const detailStr =
        typeof detail === "string"
          ? detail
          : Array.isArray(detail) && detail[0] && detail[0].msg
            ? detail[0].msg
            : typeof detail === "object" && detail !== null
              ? JSON.stringify(detail)
              : "";
      return (
        this._normalizeApiMessage((e && e.message) || "") ||
        this._normalizeApiMessage(p && (p.message || detailStr)) ||
        "网络异常，请稍后重试"
      );
    },

    async postJson(url, body) {
      let res;
      try {
        res = await fetch(url, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(body),
        });
      } catch (netErr) {
        const extra =
          netErr && typeof netErr.message === "string" && netErr.message
            ? `（${netErr.message}）`
            : "";
        const err = new Error(
          `无法连接服务器${extra}。请确认已 npm run serve（端口见 vue.config.js）或 Docker 客户端端口；未设置 VUE_APP_API_BASE 时应走 /api 代理到后端`
        );
        err.cause = netErr;
        throw err;
      }
      const text = await res.text();
      let data = null;
      if (text) {
        try {
          data = JSON.parse(text);
        } catch {
          const err = new Error(
            !res.ok
              ? `服务返回非 JSON（HTTP ${res.status}），多为代理地址错误或后端未启动`
              : "接口返回非 JSON"
          );
          err.rawPreview = text.slice(0, 160);
          throw err;
        }
      } else {
        data = {};
      }
      if (data === null || typeof data !== "object") {
        const err = new Error("接口返回数据异常");
        err.payload = data;
        throw err;
      }
      if (!res.ok) {
        const msg =
          data.message ||
          (typeof data.detail === "string"
            ? data.detail
            : data.detail
              ? JSON.stringify(data.detail)
              : "") ||
          `请求失败（${res.status}）`;
        const err = new Error(msg);
        err.payload = data;
        throw err;
      }
      return data;
    },
    switchMode(nextMode) {
      if (nextMode !== "login" && nextMode !== "register") return
      this.fieldErrors = {}
      this.mode = nextMode
    },
    clearFieldError(field) {
      if (this.fieldErrors[field]) {
        this.$delete(this.fieldErrors, field)
      }
    },
    onPhoneInput() {
      this.clearFieldError("phone")
      const phone = (this.activePhone || "").trim()
      if (phone.length === 11) {
        this.validatePhoneOnBlur()
      }
    },
    validatePhoneOnBlur() {
      const phone = (this.activePhone || "").trim()
      if (!phone) return
      if (!/^1[3-9]\d{9}$/.test(phone)) {
        this.focusFieldError("phone", "请输入正确的手机号")
      }
    },
    focusFieldError(field, message) {
      this.$set(this.fieldErrors, field, message)
      this.$nextTick(() => {
        requestAnimationFrame(() => {
          scrollToFormField(this, field, { navOffset: 0 })
        })
      })
    },
    validateLoginForm() {
      const phone = (this.loginForm.phone || "").trim()
      if (!phone) {
        return { field: "phone", message: "请输入手机号" }
      }
      if (!/^1[3-9]\d{9}$/.test(phone)) {
        return { field: "phone", message: "请输入正确的手机号" }
      }
      const c = (this.loginForm.code || "").trim()
      if (!c) {
        return { field: "code", message: "请输入验证码" }
      }
      if (c.length < 4 || c.length > 6) {
        return { field: "code", message: "请输入正确的验证码（4-6位）" }
      }
      return null
    },
    validateRegisterForm() {
      const userName = (this.registerForm.user_name || "").trim()
      const phone = (this.registerForm.phone || "").trim()
      if (!userName) {
        return { field: "user_name", message: "请填写姓名" }
      }
      if (!phone) {
        return { field: "phone", message: "请输入手机号" }
      }
      if (!/^1[3-9]\d{9}$/.test(phone)) {
        return { field: "phone", message: "请输入正确的手机号" }
      }
      return null
    },
    statusText(userInfo) {
      const code = Number(userInfo && userInfo.is_auth)
      if (code === 2) return "审核通过"
      if (code === 1) return "正在审核"
      if (code === 3) return "认证失败"
      if (code === 4) return "等待用户认证"
      return "未认证"
    },

    startCountdown() {
      if (this.countdownTimer) {
        clearInterval(this.countdownTimer);
      }
      this.countdown = 60;
      this.isCounting = true;
      this.countdownTimer = setInterval(() => {
        this.countdown--;
        if (this.countdown <= 0) {
          clearInterval(this.countdownTimer);
          this.countdownTimer = null;
          this.isCounting = false;
          this.countdown = 60;
        }
      }, 1000);
    },

    async handleGetCode() {
      const phone = (this.activePhone || "").trim();
      if (!phone) {
        this.focusFieldError("phone", "请输入手机号");
        return;
      }
      if (!this.isPhoneValid) {
        this.focusFieldError("phone", "请输入正确的手机号");
        return;
      }
      if (this.getCodeLoading) return;

      this.getCodeLoading = true;
      try {
        const data = await this.postJson(loginPhoneUrl(), {
          task_id: 0,
          phone_number: phone,
          biz_type: this.mode,
          is_register: this.mode === "register",
        });
        if (data.success) {
          Toast.success(data.message || "验证码已发送");
          this.startCountdown();
        } else {
          Toast.fail(this._normalizeApiMessage(data.message) || "发送失败");
        }
      } catch (e) {
        Toast.fail(this._toastApiError('服务器错误'));
      } finally {
        this.getCodeLoading = false;
      }
    },

    async handleLogin() {
      const invalid = this.validateLoginForm()
      if (invalid) {
        this.focusFieldError(invalid.field, invalid.message)
        return
      }
      const c = (this.loginForm.code || "").trim();
      if (this.loginLoading) return;

      this.loginLoading = true;
      try {
        const data = await this.postJson(loginPhoneUrl(), {
          task_id: 1,
          phone_number: this.loginForm.phone.trim(),
          verify_code: String(c),
        });
        if (data.success) {
          if (!data.user_info) {
            Toast.fail("登录成功但未返回用户信息，请稍后重试");
            return;
          }
          // 登录接口不返回认证图/大头像；个人中心进入后异步拉取
          const userInfo = slimLoginUserInfo(data.user_info);
          const saved = saveLoginSession(
            userInfo,
            userInfo.first_login || data.first_login
          );
          if (!saved.ok) {
            Toast.fail(saved.message || "登录状态保存失败");
            return;
          }
          if (Number(userInfo.is_auth) === 2) {
            markCertSnapshotPending();
          }
          this.$router.replace({ name: "user" });
          Toast.success(data.message || "登录成功");
        } else {
          Toast.fail(this._normalizeApiMessage(data.message) || "登录失败");
        }
      } catch (e) {
        Toast.fail(this._toastApiError(e));
      } finally {
        this.loginLoading = false;
      }
    },
    async handleRegisterNext() {
      const invalid = this.validateRegisterForm()
      if (invalid) {
        this.focusFieldError(invalid.field, invalid.message)
        return
      }
      const userName = (this.registerForm.user_name || "").trim()
      const phone = (this.registerForm.phone || "").trim()
      if (this.loginLoading) return
      this.loginLoading = true
      try {
        sessionStorage.setItem(
          "h5_register_draft",
          JSON.stringify({
            user_name: userName,
            phone_number: phone,
          })
        )
        this.$router.push({ name: "registerSurvey" })
      } catch (e) {
        Toast.fail("无法进入问卷页，请稍后重试")
      } finally {
        this.loginLoading = false
      }
    }
  },
};
</script>

<style scoped>
/* 整体页面 */
.login-page {
  min-height: 100vh;
  background: linear-gradient(180deg, #121535 0%, #6c5ce7 100%);
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 40px 24px 20px;
  box-sizing: border-box;
  font-family: "PingFang SC", "Helvetica Neue", sans-serif;
  position: relative;
}
.login-page.page-is-busy {
  overflow: hidden;
}
.page-loading-mask {
  position: fixed;
  inset: 0;
  z-index: 1200;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(18, 21, 53, 0.42);
  backdrop-filter: blur(2px);
}
.page-loading-box {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  padding: 22px 28px;
  border-radius: 14px;
  background: rgba(255, 255, 255, 0.12);
  border: 1px solid rgba(255, 255, 255, 0.18);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.18);
}
.page-loading-spinner {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  border: 3px solid rgba(255, 255, 255, 0.25);
  border-top-color: #fff;
  animation: page-spin 0.75s linear infinite;
}
.page-loading-text {
  margin: 0;
  color: #fff;
  font-size: 14px;
  letter-spacing: 0.02em;
}
@keyframes page-spin {
  to { transform: rotate(360deg); }
}
.login-card.is-dimmed {
  opacity: 0.72;
  pointer-events: none;
  transition: opacity 0.2s ease;
}
.btn-press:active:not(:disabled) {
  transform: scale(0.98);
}

/* 顶部品牌 */
.header {
  text-align: center;
  margin-bottom: 40px;
}
.logo-img {
  display: block;
  max-width: 280px;
  width: 100%;
  height: auto;
  margin: 0 auto;
}
.sub-title {
  font-size: 22px;
  color: #e0e0ff;
  margin: 8px 0 4px;
}
.desc {
  font-size: 15px;
  color: #ccccff;
  margin: 0;
}

/* 登录卡片 */
.login-card {
  width: 100%;
  max-width: 360px;
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  border-radius: 16px;
  padding: 32px 24px;
  box-sizing: border-box;
}
.card-title {
  color: #fff;
  text-align: center;
  margin: 0 0 28px;
  font-size: 18px;
  font-weight: 500;
}

/* 输入框组 */
.input-group {
  margin-bottom: 16px;
}
.phone-input,
.code-input {
  width: 100%;
  height: 48px;
  border-radius: 8px;
  border: none;
  outline: none;
  padding: 0 16px;
  font-size: 16px;
  box-sizing: border-box;
  background: rgba(255, 255, 255, 0.9);
}
.code-group {
  display: flex;
  gap: 10px;
  align-items: flex-start;
}
.code-input-wrap {
  flex: 1;
  min-width: 0;
}
.code-input {
  flex: 1;
  width: 100%;
}
.input-group.has-error .phone-input,
.input-group.has-error .code-input {
  box-shadow: 0 0 0 2px #ff6b81 inset;
  background: #fff5f7;
  animation: field-shake 0.45s ease;
}
.field-error-tip {
  margin: 6px 0 0;
  padding-left: 2px;
  color: #ffb8c6;
  font-size: 12px;
  line-height: 1.4;
  animation: field-tip-in 0.28s ease;
}
@keyframes field-shake {
  0%, 100% { transform: translateX(0); }
  20%, 60% { transform: translateX(-5px); }
  40%, 80% { transform: translateX(5px); }
}
@keyframes field-tip-in {
  from {
    opacity: 0;
    transform: translateY(-4px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
.code-btn {
  white-space: nowrap;
  height: 48px;
  padding: 0 16px;
  border-radius: 8px;
  border: none;
  background: #6c5ce7;
  color: #fff;
  font-size: 14px;
  cursor: pointer;
  transition: transform 0.12s ease, opacity 0.2s ease, background 0.2s ease;
}
.code-btn:active:not(:disabled) {
  transform: scale(0.97);
}
.code-btn.is-loading {
  opacity: 0.88;
}
.code-btn:disabled {
  background: #9988dd;
  cursor: not-allowed;
}

/* 登录按钮 */
.login-btn {
  width: 100%;
  height: 52px;
  border-radius: 26px;
  border: none;
  background: linear-gradient(90deg, #ffffff, #e0e7ff);
  color: #4b3f99;
  font-size: 18px;
  font-weight: bold;
  margin-top: 12px;
  cursor: pointer;
  transition: transform 0.12s ease, opacity 0.2s ease;
}
.login-btn.is-loading {
  opacity: 0.82;
}
.login-btn:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}
.switch-btn {
  width: 100%;
  height: 44px;
  margin-top: 10px;
  border-radius: 22px;
  border: 1px solid rgba(255, 255, 255, 0.55);
  background: transparent;
  color: #fff;
  font-size: 16px;
  cursor: pointer;
  transition: transform 0.12s ease, opacity 0.2s ease, background 0.2s ease;
}
.switch-btn:active:not(:disabled) {
  background: rgba(255, 255, 255, 0.08);
}
.tip {
  text-align: center;
  color: #ddd;
  font-size: 12px;
  margin-top: 16px;
}

/* 底部平台图标 */
.platforms {
  display: flex;
  gap: 24px;
  margin-top: auto;
  padding: 20px 0;
}
.platform-icon {
  width: 48px;
  height: 48px;
  border-radius: 10px;
  background: #fff;
  opacity: 0.9;
}
/* 这里用背景色模拟原图标效果，实际项目可替换为真实图片 */
.b站 {
  background: #fb7299;
}
.os {
  background: #fff;
}
.finance {
  background: #e74c3c;
}
.school {
  background: #fff;
}
.union {
  background: #fff;
}
</style>